import React, { useEffect, useState } from 'react';
import { getRecycleBinOrders, restoreOrder, permanentlyDeleteOrder } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { formatDate, getStatusColor, isCancelledOrder } from '../lib/utils';
import { Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useScrollRestoration } from '../hooks/useScreenState';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function RecycleBin() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [orderToPurge, setOrderToPurge] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'Super Admin';

  useScrollRestoration('recycleBin', !loading);

  useEffect(() => {
    if (isAdmin) fetchRecycleBin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecycleBin = async () => {
    setLoading(true);
    try {
      const response = await getRecycleBinOrders();
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch recycle bin:', error);
      toast.error('Failed to load recycle bin');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (order) => {
    try {
      await restoreOrder(order.id);
      toast.success(`Order ${order.order_details.indent_number} restored`);
      setOrders((prev) => prev.filter((o) => o.id !== order.id));
    } catch (error) {
      console.error('Failed to restore order:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore order');
    }
  };

  const handlePurgeClick = (order) => {
    setOrderToPurge(order);
    setPurgeDialogOpen(true);
  };

  const handlePurgeConfirm = async () => {
    if (!orderToPurge) return;
    try {
      await permanentlyDeleteOrder(orderToPurge.id);
      toast.success('Order permanently deleted');
      setOrders((prev) => prev.filter((o) => o.id !== orderToPurge.id));
    } catch (error) {
      console.error('Failed to permanently delete order:', error);
      toast.error(error.response?.data?.detail || 'Failed to permanently delete order');
    } finally {
      setPurgeDialogOpen(false);
      setOrderToPurge(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="recycle-bin-access-denied">
        <div className="text-center">
          <Trash2 className="h-16 w-16 text-stone-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-maroon-950 mb-2">Access Denied</h2>
          <p className="text-maroon-700">Only admins can access the Recycle Bin</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-maroon-600">Loading recycle bin...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="recycle-bin-page">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-maroon-950">Recycle Bin</h1>
        <p className="text-maroon-600 text-sm lg:text-base mt-1">
          Deleted orders are kept here until restored or permanently deleted
        </p>
      </div>

      {orders.length === 0 ? (
        <Card className="border-maroon-200">
          <CardContent className="p-10 lg:p-9 text-center">
            <Trash2 className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-maroon-600">Recycle Bin is empty</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:gap-4">
          {orders.map((order) => {
            const isCancelled = isCancelledOrder(order.order_details.status);
            return (
              <Card key={order.id} className="border-maroon-200" data-testid={`recycle-bin-card-${order.id}`}>
                <CardContent className="p-4 lg:p-5">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between lg:justify-start gap-2 lg:gap-3 mb-2">
                        <h3 className="text-lg lg:text-2xl font-semibold text-maroon-950">
                          {order.order_details.indent_number}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs rounded-md border flex-shrink-0 ${getStatusColor(
                            order.order_details.status
                          )}`}
                        >
                          {order.order_details.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2 lg:gap-4 text-sm lg:text-base mt-3">
                        <div className="min-w-0">
                          <span className="text-xs lg:text-sm text-maroon-500">Date</span>
                          <p className="font-medium truncate text-gray-900">
                            {formatDate(order.order_details.order_date)}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs lg:text-sm text-maroon-500">Buyers</span>
                          <p className="font-medium truncate text-gray-900">
                            {order.buyers.map((b) => b.buyer_name).join(', ') || 'N/A'}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs lg:text-sm text-maroon-500">Deleted By</span>
                          <p className="font-medium truncate text-gray-900">{order.deleted_by || 'N/A'}</p>
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs lg:text-sm text-maroon-500">Deleted On</span>
                          <p className="font-medium truncate text-gray-900">
                            {order.deleted_at ? formatDate(order.deleted_at) : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end lg:ml-4 flex-shrink-0">
                      <Button
                        data-testid={`restore-order-${order.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestore(order)}
                        className="border-green-300 text-green-700 hover:bg-green-50 transition-colors duration-200"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                      <Button
                        data-testid={`permanent-delete-order-${order.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePurgeClick(order)}
                        className="border-red-300 text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Forever
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={purgeDialogOpen} onOpenChange={setPurgeDialogOpen}>
        <AlertDialogContent className="bg-white" data-testid="permanent-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Permanently Delete Order
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete order &quot;{orderToPurge?.order_details.indent_number}
              &quot;? This action cannot be undone and the order cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="permanent-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="permanent-delete-confirm-btn"
              onClick={handlePurgeConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
